/* Louz - Skin AI camera capture plumbing. Vanilla ES6, no dependencies. */
(function () {
  'use strict';

  function asCallback(fn) {
    return typeof fn === 'function' ? fn : function () {};
  }

  function stopStream(stream) {
    if (!stream || typeof stream.getTracks !== 'function') return;
    stream.getTracks().forEach(function (track) {
      if (track && typeof track.stop === 'function') track.stop();
    });
  }

  var CENTER_MIN = 0.3;
  var CENTER_MAX = 0.7;
  var YAW_SYMMETRY_MIN = 0.65;
  var DETECTION_INTERVAL_MS = 250;
  var AUTO_CAPTURE_HOLD_MS = 3000;
  var MANUAL_SHUTTER_DELAY_MS = 5000;
  var MAX_CONSECUTIVE_FAILED_TICKS = 5;
  var CAPTURE_BLOB_TIMEOUT_MS = 5000;
  var FRAME_ANALYSIS_WIDTH = 192;
  var FRAME_ANALYSIS_HEIGHT = 144;
  var BRIGHTNESS_DARK_MAX = 60;
  var BRIGHTNESS_BRIGHT_MIN = 200;
  var BLUR_VARIANCE_MIN = 15;
  var LAPLACIAN_SAMPLE_STEP = 2;
  var MEDIAPIPE_INIT_TIMEOUT_MS = 8000;
  var NOSE_TIP_INDEX = 1;
  var LEFT_EYE_OUTER_INDEX = 263;
  var RIGHT_EYE_OUTER_INDEX = 33;
  var LIVE_DOT_SEARCH_RGB = '243, 147, 86';
  var LIVE_DOT_LOCKED_RGB = '46, 125, 82';
  var LIVE_DOT_INDICES = [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288,
    397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136,
    172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
    263, 390, 374, 362, 386, 388, 33, 163, 145, 133, 159, 161,
    276, 282, 295, 285, 46, 52, 65, 55,
    61, 91, 84, 17, 314, 321, 291, 270, 267, 0, 37, 40,
    168, 197, 5, 4, 1
  ];
  var LIVE_DOT_ANCHOR_INDICES = [
    NOSE_TIP_INDEX,
    LEFT_EYE_OUTER_INDEX,
    RIGHT_EYE_OUTER_INDEX
  ];
  var mediaPipeVisionLoadPromise = null;

  function validateMediaPipeUrls(urls) {
    return urls &&
      typeof urls.bundle === 'string' &&
      typeof urls.wasmLoader === 'string' &&
      typeof urls.wasmBinary === 'string' &&
      typeof urls.model === 'string';
  }

  function withTimeout(promise, timeoutMs) {
    var timeout = null;
    var timeoutPromise = new Promise(function (_resolve, reject) {
      timeout = window.setTimeout(function () {
        reject(new Error('MediaPipe initialization timed out'));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).then(function (value) {
      window.clearTimeout(timeout);
      return value;
    }, function (error) {
      window.clearTimeout(timeout);
      throw error;
    });
  }

  function closeLandmarker(landmarker) {
    if (landmarker && typeof landmarker.close === 'function') landmarker.close();
  }

  function distance2d(a, b) {
    var dx = a.x - b.x;
    var dy = a.y - b.y;
    return Math.sqrt((dx * dx) + (dy * dy));
  }

  function getFaceState(result) {
    var faceLandmarks = result && result.faceLandmarks ? result.faceLandmarks : [];
    var landmarks = faceLandmarks[0];
    var facePresent = faceLandmarks.length > 0;
    var centered = false;
    var angleOk = false;

    if (landmarks && landmarks.length) {
      var sumX = 0;
      var sumY = 0;

      landmarks.forEach(function (landmark) {
        sumX += landmark.x;
        sumY += landmark.y;
      });

      var centroidX = sumX / landmarks.length;
      var centroidY = sumY / landmarks.length;

      centered = centroidX >= CENTER_MIN &&
        centroidX <= CENTER_MAX &&
        centroidY >= CENTER_MIN &&
        centroidY <= CENTER_MAX;

      var noseTip = landmarks[NOSE_TIP_INDEX];
      var leftEyeOuter = landmarks[LEFT_EYE_OUTER_INDEX];
      var rightEyeOuter = landmarks[RIGHT_EYE_OUTER_INDEX];

      if (noseTip && leftEyeOuter && rightEyeOuter) {
        var leftDistance = distance2d(noseTip, leftEyeOuter);
        var rightDistance = distance2d(noseTip, rightEyeOuter);
        var maxDistance = Math.max(leftDistance, rightDistance);
        var minDistance = Math.min(leftDistance, rightDistance);

        angleOk = maxDistance > 0 && (minDistance / maxDistance) >= YAW_SYMMETRY_MIN;
      }
    }

    return {
      facePresent: facePresent,
      centered: centered,
      angleOk: angleOk
    };
  }

  function getFrameQualityState(context, grayscale) {
    var width = FRAME_ANALYSIS_WIDTH;
    var height = FRAME_ANALYSIS_HEIGHT;
    var imageData = context.getImageData(0, 0, width, height).data;
    var luminanceTotal = 0;
    var pixelCount = width * height;
    var i;
    var pixelIndex;

    for (i = 0; i < imageData.length; i += 4) {
      pixelIndex = i / 4;
      var luminance = (0.299 * imageData[i]) +
        (0.587 * imageData[i + 1]) +
        (0.114 * imageData[i + 2]);

      grayscale[pixelIndex] = luminance;
      luminanceTotal += luminance;
    }

    var meanLuminance = luminanceTotal / pixelCount;
    var laplacianCount = 0;
    var laplacianMean = 0;
    var laplacianM2 = 0;
    var x;
    var y;

    for (y = 1; y < height - 1; y += LAPLACIAN_SAMPLE_STEP) {
      for (x = 1; x < width - 1; x += LAPLACIAN_SAMPLE_STEP) {
        pixelIndex = (y * width) + x;
        var laplacian = (4 * grayscale[pixelIndex]) -
          grayscale[pixelIndex - width] -
          grayscale[pixelIndex + width] -
          grayscale[pixelIndex - 1] -
          grayscale[pixelIndex + 1];
        laplacianCount += 1;
        var delta = laplacian - laplacianMean;
        laplacianMean += delta / laplacianCount;
        laplacianM2 += delta * (laplacian - laplacianMean);
      }
    }

    var laplacianVariance = laplacianCount > 0 ? laplacianM2 / laplacianCount : 0;

    return {
      brightnessTooDark: meanLuminance < BRIGHTNESS_DARK_MAX,
      brightnessTooBright: meanLuminance > BRIGHTNESS_BRIGHT_MIN,
      blurOk: laplacianVariance >= BLUR_VARIANCE_MIN
    };
  }

  function resolveStatusKey(faceState, frameQualityState) {
    if (!faceState.facePresent) return 'NO_FACE_DETECTED';
    if (!faceState.centered) return 'FACE_NOT_CENTERED';
    if (!faceState.angleOk) return 'FACE_ANGLE_TOO_EXTREME';
    if (frameQualityState.brightnessTooDark) return 'IMAGE_DARK';
    if (frameQualityState.brightnessTooBright) return 'IMAGE_OVEREXPOSED';
    if (!frameQualityState.blurOk) return 'IMAGE_BLURRY';
    return null;
  }

  function clamp01(value) {
    return Math.max(0, Math.min(1, value));
  }

  function mapVideoLandmarkToCanvasPoint(landmark, video, canvasCssWidth, canvasCssHeight) {
    if (!landmark || !video.videoWidth || !video.videoHeight || !canvasCssWidth || !canvasCssHeight) {
      return null;
    }

    var scale = Math.max(
      canvasCssWidth / video.videoWidth,
      canvasCssHeight / video.videoHeight
    );
    var scaledVideoWidth = video.videoWidth * scale;
    var scaledVideoHeight = video.videoHeight * scale;
    var offsetX = (canvasCssWidth - scaledVideoWidth) / 2;
    var offsetY = (canvasCssHeight - scaledVideoHeight) / 2;

    return {
      x: (landmark.x * video.videoWidth * scale) + offsetX,
      y: (landmark.y * video.videoHeight * scale) + offsetY
    };
  }

  function open(opts) {
    opts = opts || {};
    var onCancel = asCallback(opts.onCancel);
    var onFallback = asCallback(opts.onFallback);
    var onStatusChange = asCallback(opts.onStatusChange);
    var onCapture = asCallback(opts.onCapture);
    var live = document.querySelector('[data-lsa-live]');

    if (!live) {
      onFallback('init_failed');
      return;
    }

    var liveDataset = live.dataset || {};
    var mediapipeUrls = {
      bundle: liveDataset.mediapipeBundleUrl,
      wasmLoader: liveDataset.mediapipeWasmLoaderUrl,
      wasmBinary: liveDataset.mediapipeWasmBinaryUrl,
      model: liveDataset.mediapipeModelUrl
    };
    var video = live.querySelector('[data-lsa-live-video]');
    var guide = live.querySelector('[data-lsa-live-guide]');
    var dotsCanvas = live.querySelector('[data-lsa-live-dots]');
    var progressRing = live.querySelector('[data-lsa-live-progress]');
    var countdown = live.querySelector('[data-lsa-live-countdown]');
    var close = live.querySelector('[data-lsa-live-close]');
    var shutter = live.querySelector('[data-lsa-live-shutter]');
    var status = live.querySelector('[data-lsa-live-status]');

    if (!video || !guide || !dotsCanvas || !progressRing || !countdown || !close || !shutter || !status) {
      onFallback('init_failed');
      return;
    }

    var stream = null;
    var closed = false;
    var capturing = false;
    var detectionTimer = null;
    var manualShutterTimer = null;
    var manualShutterEnabled = false;
    var landmarker = null;
    var faceState = {
      facePresent: false,
      centered: false,
      angleOk: false
    };
    var lastLoggedFaceState = null;
    var lastGeometryLocked = null;
    var lastProgressActive = null;
    var lastStatusKey = null;
    var hasLastStatusKey = false;
    var hasStatusTick = false;
    var consecutivePassingTicks = 0;
    var consecutiveFailedTicks = 0;
    var autoCaptureRequiredTicks = Math.ceil(AUTO_CAPTURE_HOLD_MS / DETECTION_INTERVAL_MS);
    var frameAnalysisCanvas = document.createElement('canvas');
    var frameAnalysisGrayscale = new Float32Array(FRAME_ANALYSIS_WIDTH * FRAME_ANALYSIS_HEIGHT);
    var dotsContext = dotsCanvas.getContext('2d');
    var dotsCanvasCssWidth = 0;
    var dotsCanvasCssHeight = 0;

    frameAnalysisCanvas.width = FRAME_ANALYSIS_WIDTH;
    frameAnalysisCanvas.height = FRAME_ANALYSIS_HEIGHT;
    var frameAnalysisContext = frameAnalysisCanvas.getContext('2d', { willReadFrequently: true });

    function langKey() {
      var root = document.querySelector('[data-lsa]');
      var locale = (root && root.dataset && root.dataset.locale) ||
        document.documentElement.lang ||
        'ar';
      return String(locale).toLowerCase().indexOf('ar') === 0 ? 'ar' : 'en';
    }

    function liveCopy() {
      var lsa = window.LouzSkinAI || {};
      var i18n = lsa.I18N || {};
      return i18n[langKey()] || i18n.ar || {};
    }

    function statusTextFor(statusKey) {
      var copy = liveCopy();

      if (!hasStatusTick) return copy.liveCaptureIdlePrompt || '';
      if (statusKey === null) return copy.liveCaptureHoldPrompt || '';
      return (copy.errors && copy.errors[statusKey]) || '';
    }

    function syncStatus(statusKey) {
      status.textContent = statusTextFor(statusKey);
    }

    function resizeDotsCanvas() {
      if (!dotsContext || !video.videoWidth || !video.videoHeight) return;

      var rect = dotsCanvas.getBoundingClientRect();
      var cssWidth = rect.width || dotsCanvas.clientWidth;
      var cssHeight = rect.height || dotsCanvas.clientHeight;

      if (!cssWidth || !cssHeight) return;

      var dpr = window.devicePixelRatio || 1;
      var pixelWidth = Math.round(cssWidth * dpr);
      var pixelHeight = Math.round(cssHeight * dpr);

      dotsCanvasCssWidth = cssWidth;
      dotsCanvasCssHeight = cssHeight;

      if (dotsCanvas.width !== pixelWidth || dotsCanvas.height !== pixelHeight) {
        dotsCanvas.width = pixelWidth;
        dotsCanvas.height = pixelHeight;
      }

      dotsContext.setTransform(dpr, 0, 0, dpr, 0, 0);
      clearDotsCanvas();
    }

    function clearDotsCanvas() {
      if (!dotsContext) return;
      dotsContext.clearRect(0, 0, dotsCanvasCssWidth || dotsCanvas.width, dotsCanvasCssHeight || dotsCanvas.height);
    }

    function drawDot(landmarks, index, radius, fillStyle) {
      var point = mapVideoLandmarkToCanvasPoint(
        landmarks[index],
        video,
        dotsCanvasCssWidth,
        dotsCanvasCssHeight
      );

      if (!point) return;

      dotsContext.beginPath();
      dotsContext.arc(point.x, point.y, radius, 0, Math.PI * 2);
      dotsContext.fillStyle = fillStyle;
      dotsContext.fill();
    }

    function drawLandmarkDots(landmarks, geometryLocked) {
      if (!dotsContext || !landmarks || !landmarks.length) return;

      if (!dotsCanvasCssWidth || !dotsCanvasCssHeight) {
        resizeDotsCanvas();
      }

      if (!dotsCanvasCssWidth || !dotsCanvasCssHeight) return;

      clearDotsCanvas();

      var color = geometryLocked ? LIVE_DOT_LOCKED_RGB : LIVE_DOT_SEARCH_RGB;
      var dotOpacity = geometryLocked ? 0.85 : 0.5;
      var dotFill = 'rgba(' + color + ', ' + dotOpacity + ')';
      var anchorFill = 'rgba(' + color + ', 1)';
      var i;

      for (i = 0; i < LIVE_DOT_INDICES.length; i += 1) {
        drawDot(landmarks, LIVE_DOT_INDICES[i], 1.75, dotFill);
      }

      for (i = 0; i < LIVE_DOT_ANCHOR_INDICES.length; i += 1) {
        drawDot(landmarks, LIVE_DOT_ANCHOR_INDICES[i], 3, anchorFill);
      }
    }

    function syncGuideLocked(geometryLocked) {
      if (lastGeometryLocked === geometryLocked) return;
      lastGeometryLocked = geometryLocked;
      guide.classList.toggle('is-locked', geometryLocked);
    }

    function syncCaptureProgress() {
      var progress = clamp01(consecutivePassingTicks / autoCaptureRequiredTicks);
      var progressActive = consecutivePassingTicks > 0;
      var remainingTicks = autoCaptureRequiredTicks - consecutivePassingTicks;
      var countdownActive = progressActive && remainingTicks <= 3;

      guide.style.setProperty('--lsa-live-progress', String(progress));

      if (lastProgressActive !== progressActive) {
        lastProgressActive = progressActive;
        progressRing.classList.toggle('is-active', progressActive);
      }

      countdown.classList.toggle('is-active', countdownActive);

      if (countdownActive) {
        countdown.textContent = String(Math.max(1, remainingTicks));
      }
    }

    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    syncStatus(null);
    syncGuideLocked(false);
    syncCaptureProgress();
    live.removeAttribute('hidden');

    function stopDetection() {
      if (detectionTimer) {
        window.clearInterval(detectionTimer);
        detectionTimer = null;
      }
      if (manualShutterTimer) {
        window.clearTimeout(manualShutterTimer);
        manualShutterTimer = null;
      }
      closeLandmarker(landmarker);
      landmarker = null;
    }

    function cleanup() {
      stopDetection();
      stopStream(stream);
      stream = null;
      video.srcObject = null;
      close.removeEventListener('click', cancel);
      shutter.removeEventListener('click', handleManualShutter);
      video.removeEventListener('loadedmetadata', resizeDotsCanvas);
      window.removeEventListener('resize', resizeDotsCanvas);
      clearDotsCanvas();
      guide.classList.remove('is-locked');
      guide.style.setProperty('--lsa-live-progress', '0');
      progressRing.classList.remove('is-active');
      countdown.classList.remove('is-active');
      countdown.textContent = '';
      status.textContent = '';
      shutter.setAttribute('hidden', '');
      live.setAttribute('hidden', '');
    }

    function cancel() {
      if (closed || capturing) return;
      closed = true;
      cleanup();
      onCancel();
    }

    function handleManualShutter() {
      if (closed || capturing || !manualShutterEnabled) return;
      console.log('[louz-skin-ai-capture] Manual shutter triggered');
      captureCurrentFrame();
    }

    function fallback(reason) {
      if (closed) return;
      closed = true;
      cleanup();
      onFallback(reason);
    }

    function createFaceLandmarker() {
      if (!validateMediaPipeUrls(mediapipeUrls)) {
        return Promise.reject(new Error('Missing MediaPipe URLs'));
      }

      function loadMediaPipeVisionBundle() {
        if (window.Vision) return Promise.resolve(window.Vision);
        if (mediaPipeVisionLoadPromise) return mediaPipeVisionLoadPromise;

        mediaPipeVisionLoadPromise = new Promise(function (resolve, reject) {
          var script = document.createElement('script');
          script.src = mediapipeUrls.bundle;
          script.onload = function () {
            resolve(window.Vision);
          };
          script.onerror = function () {
            mediaPipeVisionLoadPromise = null;
            reject(new Error('Failed to load MediaPipe bundle script'));
          };
          document.head.appendChild(script);
        });

        return mediaPipeVisionLoadPromise;
      }

      return loadMediaPipeVisionBundle().then(function (vision) {
        if (!vision || !vision.FaceLandmarker) {
          throw new Error('MediaPipe FaceLandmarker export missing');
        }

        var fileset = {
          wasmLoaderPath: mediapipeUrls.wasmLoader,
          wasmBinaryPath: mediapipeUrls.wasmBinary
        };
        var gpuOptions = {
          baseOptions: {
            modelAssetPath: mediapipeUrls.model,
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numFaces: 1
        };
        var cpuOptions = {
          baseOptions: {
            modelAssetPath: mediapipeUrls.model,
            delegate: 'CPU'
          },
          runningMode: 'VIDEO',
          numFaces: 1
        };

        return vision.FaceLandmarker.createFromOptions(fileset, gpuOptions)
          .catch(function (error) {
            console.log('[louz-skin-ai-capture] GPU delegate failed, using CPU delegate:', error);
            return vision.FaceLandmarker.createFromOptions(fileset, cpuOptions);
          });
      });
    }

    function logFaceState(nextFaceState) {
      faceState = nextFaceState;

      if (
        !lastLoggedFaceState ||
        lastLoggedFaceState.facePresent !== faceState.facePresent ||
        lastLoggedFaceState.centered !== faceState.centered ||
        lastLoggedFaceState.angleOk !== faceState.angleOk
      ) {
        lastLoggedFaceState = {
          facePresent: faceState.facePresent,
          centered: faceState.centered,
          angleOk: faceState.angleOk
        };
        console.log(lastLoggedFaceState);
      }
    }

    function notifyStatusChange(statusKey) {
      hasStatusTick = true;
      if (!hasLastStatusKey || lastStatusKey !== statusKey) {
        hasLastStatusKey = true;
        lastStatusKey = statusKey;
        syncStatus(statusKey);
        onStatusChange(statusKey);
      }
    }

    function captureCurrentFrame() {
      capturing = true;

      var captureWidth = video.videoWidth;
      var captureHeight = video.videoHeight;

      if (!captureWidth || !captureHeight) {
        fallback('init_failed');
        return;
      }

      var captureCanvas = document.createElement('canvas');
      captureCanvas.width = captureWidth;
      captureCanvas.height = captureHeight;

      var captureContext = captureCanvas.getContext('2d');

      if (!captureContext || typeof captureCanvas.toBlob !== 'function') {
        fallback('init_failed');
        return;
      }

      try {
        captureContext.drawImage(video, 0, 0, captureWidth, captureHeight);
      } catch (_error) {
        fallback('init_failed');
        return;
      }

      var blobTimeout = window.setTimeout(function () {
        console.error('[louz-skin-ai-capture] toBlob timeout');
        fallback('init_failed');
      }, CAPTURE_BLOB_TIMEOUT_MS);

      try {
        captureCanvas.toBlob(function (blob) {
          window.clearTimeout(blobTimeout);

          if (closed) return;

          if (!blob) {
            fallback('init_failed');
            return;
          }

          closed = true;
          cleanup();
          onCapture(blob);
        }, 'image/jpeg', 0.92);
      } catch (_error) {
        window.clearTimeout(blobTimeout);
        fallback('init_failed');
      }
    }

    function runDetectionTick() {
      if (closed || capturing || !landmarker || !frameAnalysisContext) return;

      try {
        var detectionResult = landmarker.detectForVideo(video, performance.now());
        var nextFaceState = getFaceState(detectionResult);
        var landmarks = detectionResult &&
          detectionResult.faceLandmarks &&
          detectionResult.faceLandmarks[0];
        var geometryLocked = nextFaceState.facePresent &&
          nextFaceState.centered &&
          nextFaceState.angleOk;

        consecutiveFailedTicks = 0;

        syncGuideLocked(geometryLocked);

        if (nextFaceState.facePresent && landmarks) {
          drawLandmarkDots(landmarks, geometryLocked);
        } else {
          clearDotsCanvas();
        }

        frameAnalysisContext.drawImage(
          video,
          0,
          0,
          FRAME_ANALYSIS_WIDTH,
          FRAME_ANALYSIS_HEIGHT
        );
        var frameQualityState = getFrameQualityState(
          frameAnalysisContext,
          frameAnalysisGrayscale
        );

        logFaceState(nextFaceState);
        var statusKey = resolveStatusKey(nextFaceState, frameQualityState);
        notifyStatusChange(statusKey);

        if (statusKey === null) {
          consecutivePassingTicks += 1;
        } else {
          consecutivePassingTicks = 0;
        }

        syncCaptureProgress();

        if (statusKey === null && consecutivePassingTicks >= autoCaptureRequiredTicks) {
          captureCurrentFrame();
        }
      } catch (error) {
        consecutiveFailedTicks += 1;
        console.error('[louz-skin-ai-capture] Detection tick failed:', error);

        if (consecutiveFailedTicks >= MAX_CONSECUTIVE_FAILED_TICKS) {
          console.error('[louz-skin-ai-capture] Too many consecutive failures, aborting');
          fallback('init_failed');
        }
      }
    }

    function startDetectionLoop() {
      runDetectionTick();
      detectionTimer = window.setInterval(runDetectionTick, DETECTION_INTERVAL_MS);

      manualShutterTimer = window.setTimeout(function () {
        if (closed || capturing) return;
        manualShutterEnabled = true;
        shutter.removeAttribute('hidden');
        console.log('[louz-skin-ai-capture] Manual shutter enabled');
      }, MANUAL_SHUTTER_DELAY_MS);
    }

    function startMediaPipe() {
      var initPromise = createFaceLandmarker();

      initPromise.then(function (createdLandmarker) {
        if (closed) closeLandmarker(createdLandmarker);
      }).catch(function () {});

      withTimeout(initPromise, MEDIAPIPE_INIT_TIMEOUT_MS)
        .then(function (createdLandmarker) {
          if (closed) return;

          landmarker = createdLandmarker;
          startDetectionLoop();
        })
        .catch(function (error) {
          console.error('[louz-skin-ai-capture] MediaPipe init failed:', error);
          fallback('init_failed');
        });
    }

    close.addEventListener('click', cancel);
    shutter.addEventListener('click', handleManualShutter);
    video.addEventListener('loadedmetadata', resizeDotsCanvas);
    window.addEventListener('resize', resizeDotsCanvas);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      fallback('no_camera');
      return;
    }

    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } })
      .then(function (mediaStream) {
        if (closed) {
          stopStream(mediaStream);
          return;
        }
        stream = mediaStream;
        video.srcObject = stream;
        resizeDotsCanvas();
        var play = video.play();
        if (play && typeof play.then === 'function') {
          play.then(startMediaPipe).catch(function (error) {
            console.error('[louz-skin-ai-capture] MediaPipe init failed:', error);
            fallback('init_failed');
          });
        } else {
          startMediaPipe();
        }
      })
      .catch(function () {
        fallback('permission_denied');
      });
  }

  window.LouzSkinAICapture = {
    open: open
  };
})();
